import React, { useEffect, useState, useRef } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress,
  IconButton, Dialog, DialogTitle, DialogContent, Button, MenuItem, TextField, Autocomplete
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';

const STATUS_OPTIONS = [
  "pending", "cancelled", "in-progress", "not-available", "completed"
];

const Discrepancies = () => {
  const [washes, setWashes] = useState([]);
  const [loading, setLoading] = useState(true);

  // State for editing status
  const [editStatusOpen, setEditStatusOpen] = useState(false);
  const [editStatusId, setEditStatusId] = useState(null);
  const [editStatusValue, setEditStatusValue] = useState('');

  // State for editing washer
  const [editWasherOpen, setEditWasherOpen] = useState(false);
  const [editWasherId, setEditWasherId] = useState(null);
  const [editWasher, setEditWasher] = useState(null);
  const [editWasherQuery, setEditWasherQuery] = useState('');
  const [editWasherOptions, setEditWasherOptions] = useState([]);
  const [editWasherLoading, setEditWasherLoading] = useState(false);
  const editWasherDebounceRef = useRef();

  useEffect(() => {
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
    const url = `${process.env.REACT_APP_SERVER_URI}/washes/list?&status=${encodeURIComponent(['cancelled'])}&interior=true&table=true&limit=1000`;
    fetch(url, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    })
      .then(res => res.json())
      .then(result => {
        const filtered = (result.data || []).filter(
          w =>
            w.status === 'not-available' ||
            (w.interior && w.status !== 'completed')
        );
        setWashes(filtered);
        setLoading(false);
      })
      .catch(() => {
        setWashes([]);
        setLoading(false);
      });
  }, []);

  // Edit status handlers
  const handleEditStatusOpen = (wash) => {
    setEditStatusId(wash._id);
    setEditStatusValue(wash.status || '');
    setEditStatusOpen(true);
  };
  const handleEditStatusSave = async () => {
    if (!editStatusId) return;
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
    await fetch(`${process.env.REACT_APP_SERVER_URI}/washes/update/${editStatusId}`, {
      method: 'POST',
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ status: editStatusValue }),
    });
    setWashes(ws =>
      ws.map(w =>
        w._id === editStatusId ? { ...w, status: editStatusValue } : w
      )
    );
    setEditStatusOpen(false);
    setEditStatusId(null);
    setEditStatusValue('');
  };

  // Edit washer handlers
  const fetchEditWasherOptions = (search = '') => {
    setEditWasherLoading(true);
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
    fetch(`${process.env.REACT_APP_SERVER_URI}/washers/options?search=${encodeURIComponent(search)}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    })
      .then(res => res.json())
      .then(result => {
        setEditWasherOptions(result || []);
        setEditWasherLoading(false);
      })
      .catch(() => {
        setEditWasherOptions([]);
        setEditWasherLoading(false);
      });
  };
  const handleEditWasherOpen = (wash) => {
    setEditWasherId(wash._id);
    setEditWasher(wash.washer ? { _id: wash.washer, name: wash.washerName } : null);
    setEditWasherQuery(wash.washerName || '');
    setEditWasherOptions(wash.washer ? [{ _id: wash.washer, name: wash.washerName }] : []);
    setEditWasherOpen(true);
    fetchEditWasherOptions(wash.washerName || '');
  };
  const handleEditWasherInputChange = (event, value) => {
    setEditWasherQuery(value);
    if (editWasherDebounceRef.current) clearTimeout(editWasherDebounceRef.current);
    editWasherDebounceRef.current = setTimeout(() => {
      fetchEditWasherOptions(value);
    }, 400);
  };
  const handleEditWasherSave = async () => {
    if (!editWasherId || !editWasher) return;
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
    await fetch(`${process.env.REACT_APP_SERVER_URI}/washes/update/${editWasherId}`, {
      method: 'POST',
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ washerId: editWasher._id, washerName: editWasher.name }),
    });
    setWashes(ws =>
      ws.map(w =>
        w._id === editWasherId ? { ...w, washer: editWasher._id, washerName: editWasher.name } : w
      )
    );
    setEditWasherOpen(false);
    setEditWasherId(null);
    setEditWasher(null);
    setEditWasherQuery('');
    setEditWasherOptions([]);
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3 }}>Discrepancies</Typography>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Car Reg No</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Washer</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {washes.map(wash => (
                <TableRow key={wash._id}>
                  <TableCell>{wash.date ? new Date(wash.date).toLocaleString() : '-'}</TableCell>
                  <TableCell>{wash.userName || '-'}</TableCell>
                  <TableCell>{wash.carRegNo || '-'}</TableCell>
                  <TableCell>
                    {wash.status || '-'}
                    <IconButton
                      size="small"
                      onClick={() => handleEditStatusOpen(wash)}
                      sx={{ ml: 1 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    {wash.washerName || '-'}
                    <IconButton
                      size="small"
                      onClick={() => handleEditWasherOpen(wash)}
                      sx={{ ml: 1 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                  <TableCell>{wash.notes || '-'}</TableCell>
                </TableRow>
              ))}
              {washes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">No discrepancies found.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edit Status Dialog */}
      <Dialog open={editStatusOpen} onClose={() => setEditStatusOpen(false)}>
        <DialogTitle>Edit Status</DialogTitle>
        <Box px={3} py={2}>
          <TextField
            select
            label="Status"
            value={editStatusValue}
            onChange={e => setEditStatusValue(e.target.value)}
            fullWidth
            margin="normal"
          >
            {STATUS_OPTIONS.map(opt => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </TextField>
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button onClick={() => setEditStatusOpen(false)} sx={{ mr: 1 }}>Cancel</Button>
            <Button onClick={handleEditStatusSave} variant="contained" color="primary" disabled={!editStatusValue}>
              Save
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Edit Washer Dialog */}
      <Dialog open={editWasherOpen} onClose={() => setEditWasherOpen(false)}>
        <DialogTitle>Edit Washer</DialogTitle>
        <Box px={3} py={2}>
          <Autocomplete
            options={editWasherOptions}
            getOptionLabel={option => option.name || ''}
            loading={editWasherLoading}
            value={editWasher}
            onChange={(event, value) => setEditWasher(value)}
            inputValue={editWasherQuery}
            onInputChange={handleEditWasherInputChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Washer"
                margin="normal"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {editWasherLoading ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button onClick={() => setEditWasherOpen(false)} sx={{ mr: 1 }}>Cancel</Button>
            <Button onClick={handleEditWasherSave} variant="contained" color="primary" disabled={!editWasher}>
              Save
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default Discrepancies;
